import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { signOut } from "firebase/auth";
import { auth, db } from "../firebase";
import { collection, getDocs, doc, deleteDoc } from "firebase/firestore";
import DataTable from "react-data-table-component";
import "../App.css";
import logo from "../assets/logo.png";
import dashboardIcon from "../assets/layout-grid.png";
import appointmentsIcon from "../assets/Tick Square.png";
import logoutIcon from "../assets/logout.png";
import userIcon from "../assets/user_avatar.png";

const DoctorsList = () => {
  const navigate = useNavigate();
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const fetchDoctors = async () => {
    try {
      setLoading(true);
      const doctorsSnapshot = await getDocs(collection(db, "doctors"));
      const doctorsList = doctorsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setDoctors(doctorsList);
    } catch (error) {
      console.error("Error fetching doctors:", error);
      setError("Failed to load doctors");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDoctors();
  }, []);

  const handleDeleteDoctor = async (doctorId, doctorName) => {
    const confirmMessage = `Are you sure you want to delete doctor "${doctorName}"?\n\nThis action cannot be undone.`;
    if (!window.confirm(confirmMessage)) {
      return;
    }

    setDeleting(true);
    setError("");
    setSuccess("");

    try {
      // Delete from Firestore
      const doctorRef = doc(db, "doctors", doctorId);
      await deleteDoc(doctorRef);
      console.log("✅ Doctor document deleted from Firestore");

      setSuccess(`Doctor "${doctorName}" deleted successfully`);
      
      // Refresh the list
      await fetchDoctors();
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(""), 3000);
    } catch (error) {
      console.error("Error deleting doctor:", error);
      let errorMessage = "Failed to delete doctor. Please try again.";
      
      if (error.code === "permission-denied") {
        errorMessage = "Permission denied. Please check Firestore security rules.";
      } else if (error.code === "unavailable") {
        errorMessage = "Firestore is unavailable. Please check your connection.";
      } else {
        errorMessage = error.message || "Failed to delete doctor. Please try again.";
      }
      
      setError(errorMessage);
    } finally {
      setDeleting(false);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      localStorage.removeItem("token");
      navigate("/login");
    } catch (error) {
      console.error("Error signing out:", error);
      localStorage.removeItem("token");
      navigate("/login");
    }
  };

  const columns = [
    {
      name: "Name",
      selector: (row) => row.name,
      sortable: true,
    },
    {
      name: "Email",
      selector: (row) => row.email,
      sortable: true,
    },
    {
      name: "Phone",
      selector: (row) => row.phone || "N/A",
      sortable: true,
    },
    {
      name: "Specialty",
      selector: (row) => row.specialty || "N/A",
      sortable: true,
    },
    {
      name: "Hospital",
      selector: (row) => row.hospitalName || "N/A",
      sortable: true,
    },
    {
      name: "Status",
      selector: (row) => (row.isActive !== false ? "Active" : "Inactive"),
      sortable: true,
    },
    {
      name: "Actions",
      cell: (row) => (
        <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
          <button
            onClick={() => navigate(`/edit-doctor/${row.id}`)}
            title="Edit Doctor"
            style={{
              padding: "8px 10px",
              backgroundColor: "#4285F4",
              color: "white",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
              fontSize: "16px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "all 0.2s ease",
              boxShadow: "0 2px 4px rgba(66, 133, 244, 0.2)",
              width: "36px",
              height: "36px",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#357ae8";
              e.currentTarget.style.transform = "translateY(-1px)";
              e.currentTarget.style.boxShadow = "0 4px 6px rgba(66, 133, 244, 0.3)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "#4285F4";
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "0 2px 4px rgba(66, 133, 244, 0.2)";
            }}
          >
            ✏️
          </button>
          <button
            onClick={() => handleDeleteDoctor(row.id, row.name)}
            disabled={deleting}
            title="Delete Doctor"
            style={{
              padding: "8px 10px",
              backgroundColor: "#dc3545",
              color: "white",
              border: "none",
              borderRadius: "6px",
              cursor: deleting ? "not-allowed" : "pointer",
              fontSize: "16px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "all 0.2s ease",
              opacity: deleting ? 0.6 : 1,
              boxShadow: "0 2px 4px rgba(220, 53, 69, 0.2)",
              width: "36px",
              height: "36px",
            }}
            onMouseEnter={(e) => {
              if (!deleting) {
                e.currentTarget.style.backgroundColor = "#c82333";
                e.currentTarget.style.transform = "translateY(-1px)";
                e.currentTarget.style.boxShadow = "0 4px 6px rgba(220, 53, 69, 0.3)";
              }
            }}
            onMouseLeave={(e) => {
              if (!deleting) {
                e.currentTarget.style.backgroundColor = "#dc3545";
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "0 2px 4px rgba(220, 53, 69, 0.2)";
              }
            }}
          >
            🗑️
          </button>
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
  ];

  return (
    <div className="appointments-container">
      <div className="sidebar">
        <img src={logo} alt="Timely Logo" className="logo-image" />
        <div className="user-profile">
          <div className="user-info">
            <h3>Kevin Beers</h3>
            <p>Support Team</p>
          </div>
        </div>
        <nav>
          <a href="/dashboard">
            <img src={dashboardIcon} alt="Dashboard" className="menu-icon" />
            Dashboard
          </a>
          <a href="/appointments">
            <img src={appointmentsIcon} alt="Appointments" className="menu-icon" />
            Appointments
          </a>
          <a href="/users">
            <img src={userIcon} alt="Users" className="menu-icon" />
            Users
          </a>
          <a href="/doctors" className="active">
            <span className="menu-icon" style={{ fontSize: "24px" }}>👨‍⚕️</span>
            Doctors
          </a>
          <a href="/" onClick={(e) => { e.preventDefault(); handleLogout(); }}>
            <img src={logoutIcon} alt="Log Out" className="menu-icon" />
            Log Out
          </a>
        </nav>
      </div>

      <div className="appointments-page">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
          <h1 className="dashboard-title">Doctors</h1>
          <button
            onClick={() => navigate("/add-doctor")}
            style={{
              padding: "10px 20px",
              backgroundColor: "#4285F4",
              color: "white",
              border: "none",
              borderRadius: "5px",
              cursor: "pointer",
              fontSize: "14px",
            }}
          >
            Add New Doctor
          </button>
        </div>

        {error && (
          <div
            style={{
              color: "#d32f2f",
              backgroundColor: "#ffebee",
              padding: "15px",
              borderRadius: "5px",
              marginBottom: "20px",
              fontSize: "14px",
            }}
          >
            {error}
          </div>
        )}

        {success && (
          <div
            style={{
              color: "#2e7d32",
              backgroundColor: "#e8f5e9",
              padding: "15px",
              borderRadius: "5px",
              marginBottom: "20px",
              fontSize: "14px",
            }}
          >
            {success}
          </div>
        )}

        <div className="appointments-table">
          <DataTable
            columns={columns}
            data={doctors}
            pagination
            highlightOnHover
            striped
            responsive
            progressPending={loading}
            progressComponent={<div style={{ padding: "20px" }}>Loading doctors...</div>}
          />
        </div>
      </div>
    </div>
  );
};

export default DoctorsList;

